import * as React from 'react';
import { Controller, FieldPath, FieldValues, useFormContext } from 'react-hook-form';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

interface FileFieldProps {
  name: string;
  label?: string;
  accept?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  onFileChange?: (file: File | null) => void;
}

export const FileField = React.forwardRef<HTMLInputElement, FileFieldProps>(
  (
    {
      name,
      label,
      accept,
      disabled = false,
      required = false,
      className,
      onFileChange,
    },
    ref
  ) => {
    const { control, formState: { errors } } = useFormContext();
    const error = errors[name];
    const inputRef = React.useRef<HTMLInputElement>(null);

    return (
      <div className={cn('space-y-2', className)}>
        {label && (
          <Label htmlFor={name}>
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
          </Label>
        )}
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <Input
              ref={inputRef}
              id={name}
              type="file"
              accept={accept}
              disabled={disabled}
              required={required}
              className={cn(error && 'border-destructive')}
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                field.onChange(file);
                onFileChange?.(file);
              }}
            />
          )}
        />
        {error && (
          <p className="text-sm text-destructive">{String(error.message)}</p>
        )}
      </div>
    );
  }
);

FileField.displayName = 'FileField';

export default FileField;
